package fr.frux.feature.profile

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.Logout
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Download
import androidx.compose.material.icons.outlined.Eco
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material.icons.outlined.Feedback
import androidx.compose.material.icons.outlined.NotificationsNone
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.PhotoCamera
import androidx.compose.material.icons.outlined.Settings
import androidx.compose.material.icons.outlined.ShoppingBasket
import androidx.compose.material.icons.outlined.VolunteerActivism
import androidx.compose.material3.Badge
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.profile_favorites_plural
import fr.frux.app.resources.profile_favorites_singular
import fr.frux.app.resources.profile_harvests_organized_plural
import fr.frux.app.resources.profile_harvests_organized_singular
import fr.frux.app.resources.profile_logout
import fr.frux.app.resources.profile_logout_all
import fr.frux.app.resources.profile_member_since
import fr.frux.app.resources.profile_nudge_dismiss
import fr.frux.app.resources.profile_nudge_enrich_cta
import fr.frux.app.resources.profile_nudge_enrich_text
import fr.frux.app.resources.profile_nudge_enrich_title
import fr.frux.app.resources.profile_nudge_first_cta
import fr.frux.app.resources.profile_nudge_first_text
import fr.frux.app.resources.profile_nudge_first_title
import fr.frux.app.resources.profile_row_activity
import fr.frux.app.resources.profile_row_activity_subtitle
import fr.frux.app.resources.profile_row_delete_account
import fr.frux.app.resources.profile_row_export
import fr.frux.app.resources.profile_row_export_subtitle
import fr.frux.app.resources.profile_row_feedback
import fr.frux.app.resources.profile_row_feedback_subtitle
import fr.frux.app.resources.profile_row_my_trees
import fr.frux.app.resources.profile_row_my_trees_subtitle
import fr.frux.app.resources.profile_row_settings
import fr.frux.app.resources.profile_row_support_subtitle
import fr.frux.app.resources.profile_row_support_title
import fr.frux.app.resources.profile_section_account
import fr.frux.app.resources.profile_section_activity
import fr.frux.app.resources.profile_title
import fr.frux.app.resources.profile_trees_shared_plural
import fr.frux.app.resources.profile_trees_shared_singular
import fr.frux.app.resources.profile_version
import fr.frux.core.common.config.appVersion
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.userprofile.formatMemberSince
import fr.frux.shared.domain.model.User
import fr.frux.shared.domain.model.UserProfile
import org.jetbrains.compose.resources.stringResource

/**
 * Une ligne de la section « Mon activité » ou « Compte » (refonte #442) : même gabarit pour les
 * deux, factorisé pour ne pas dupliquer la logique entre mobile et grand écran.
 */
internal data class ProfileRowItem(
    val icon: ImageVector,
    val title: String,
    val subtitle: String? = null,
    val badgeCount: Int? = null,
    val isLoading: Boolean = false,
    val enabled: Boolean = true,
    val iconContainerColor: Color,
    val iconTint: Color,
    val onClick: () -> Unit,
)

/**
 * Bloc unique à coins arrondis contenant plusieurs [ProfileRowItem], séparées par un trait —
 * remplace les cartes séparées par ligne de l'ancien écran (mock 2026-09-23, #442).
 */
@Composable
internal fun ProfileRowsCard(
    items: List<ProfileRowItem>,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column {
            items.forEachIndexed { index, item ->
                if (index > 0) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
                ProfileRow(item)
            }
        }
    }
}

@Composable
private fun ProfileRow(item: ProfileRowItem) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = 60.dp)
                .clickable(enabled = item.enabled && !item.isLoading, onClick = item.onClick)
                .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ProfileRowIconBadge(icon = item.icon, containerColor = item.iconContainerColor, tint = item.iconTint)
        Spacer(modifier = Modifier.width(FruxSpacing.md))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = item.title,
                style = MaterialTheme.typography.bodyLarge,
                color =
                    if (item.enabled) {
                        MaterialTheme.colorScheme.onSurface
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
            )
            item.subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        when {
            item.isLoading -> {
                CircularProgressIndicator(modifier = Modifier.size(20.dp))
            }

            !item.enabled -> {
                Unit
            }

            item.badgeCount != null && item.badgeCount > 0 -> {
                Badge(
                    containerColor = MaterialTheme.colorScheme.tertiary,
                    contentColor = MaterialTheme.colorScheme.onTertiary,
                ) {
                    Text(item.badgeCount.toString())
                }
                Spacer(modifier = Modifier.width(FruxSpacing.sm))
                ProfileChevron()
            }

            else -> {
                ProfileChevron()
            }
        }
    }
}

@Composable
private fun ProfileChevron() {
    Icon(
        imageVector = Icons.Outlined.ChevronRight,
        contentDescription = null,
        tint = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

/** Pastille ronde-carrée (coins 12dp) portant l'icône d'une ligne. */
@Composable
internal fun ProfileRowIconBadge(
    icon: ImageVector,
    containerColor: Color,
    tint: Color,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.size(40.dp),
        shape = RoundedCornerShape(12.dp),
        color = containerColor,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(imageVector = icon, contentDescription = null, tint = tint)
        }
    }
}

/** Une tuile d'activité pour la disposition grand écran (deux tuiles côte à côte, mock Web). */
@Composable
internal fun ProfileActivityTile(
    item: ProfileRowItem,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.heightIn(min = 150.dp),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        onClick = item.onClick,
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(FruxSpacing.lg)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                ProfileRowIconBadge(icon = item.icon, containerColor = item.iconContainerColor, tint = item.iconTint)
                if (item.badgeCount != null && item.badgeCount > 0) {
                    Badge(
                        containerColor = MaterialTheme.colorScheme.tertiary,
                        contentColor = MaterialTheme.colorScheme.onTertiary,
                    ) {
                        Text(item.badgeCount.toString())
                    }
                }
            }
            Spacer(modifier = Modifier.height(FruxSpacing.md))
            Text(
                text = item.title,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
            item.subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

/** En-tête de section (« MON ACTIVITÉ », « COMPTE ») : petites capitales espacées. */
@Composable
internal fun ProfileSectionHeader(
    title: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = title,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = modifier.padding(horizontal = FruxSpacing.xs),
    )
}

/** Contenu d'une tuile de compteur (#156, refonte #442). */
internal data class ContributionTileData(
    val icon: ImageVector,
    val count: Int,
    val label: String,
    val iconContainerColor: Color,
    val iconTint: Color,
)

/**
 * Compteurs de contribution. [translucent] bascule vers le rendu du bandeau grand écran (tuiles
 * translucides sur le vert forêt). Sinon, une seule carte blanche à trois colonnes séparées par
 * des filets (#591, piste B) : une ombre et un rayon, pas trois blocs étroits qui coupent les
 * libellés.
 */
@Composable
internal fun ProfileContributionTiles(
    items: List<ContributionTileData>,
    modifier: Modifier = Modifier,
    translucent: Boolean = false,
) {
    if (translucent) {
        // Hauteur intrinsèque : un libellé qui passe sur deux lignes (« récolte organisée ») grandit
        // toute la rangée au lieu de sa seule tuile, les tuiles restent de mêmes dimensions.
        Row(
            modifier = modifier.fillMaxWidth().height(IntrinsicSize.Min),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            items.forEach { item ->
                ProfileContributionTile(
                    item = item,
                    translucent = true,
                    modifier = Modifier.weight(1f).fillMaxHeight(),
                )
            }
        }
    } else {
        Surface(
            modifier = modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            shadowElevation = 2.dp,
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min).padding(vertical = FruxSpacing.sm),
            ) {
                items.forEachIndexed { index, item ->
                    if (index > 0) {
                        VerticalDivider(
                            modifier = Modifier.fillMaxHeight(),
                            color = MaterialTheme.colorScheme.outlineVariant,
                        )
                    }
                    ProfileContributionTile(
                        item = item,
                        translucent = false,
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                    )
                }
            }
        }
    }
}

@Composable
private fun ProfileContributionTile(
    item: ContributionTileData,
    translucent: Boolean,
    modifier: Modifier = Modifier,
) {
    if (translucent) {
        Surface(
            modifier = modifier,
            shape = MaterialTheme.shapes.medium,
            color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.08f),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.12f)),
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(FruxSpacing.sm).semantics(mergeDescendants = true) {},
            ) {
                Text(
                    text = item.count.toString(),
                    style = MaterialTheme.typography.headlineSmall,
                    color = MaterialTheme.colorScheme.onPrimary,
                )
                Text(
                    text = item.label,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onPrimary,
                )
            }
        }
    } else {
        // Une colonne de la carte unique : icône, chiffre et libellé centrés, le libellé peut
        // passer sur deux lignes sans décaler les colonnes voisines (hauteur intrinsèque du Row).
        Column(
            modifier =
                modifier
                    .padding(horizontal = FruxSpacing.xs, vertical = FruxSpacing.xs)
                    .semantics(mergeDescendants = true) {},
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            ProfileRowIconBadge(
                icon = item.icon,
                containerColor = item.iconContainerColor,
                tint = item.iconTint,
                modifier = Modifier.size(32.dp),
            )
            Spacer(modifier = Modifier.height(FruxSpacing.xs))
            Text(
                text = item.count.toString(),
                style = MaterialTheme.typography.headlineSmall,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = item.label,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
    }
}

/**
 * Carte d'accompagnement du profil : rappel du premier arbre, puis invitation à enrichir ceux qu'on
 * a déjà. Aucun chiffre (ADR 0020).
 */
@Composable
internal fun ContributionNudgeCard(
    nudge: ContributionNudge,
    onPrimary: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val first = nudge == ContributionNudge.FIRST_TREE
    val title = if (first) Res.string.profile_nudge_first_title else Res.string.profile_nudge_enrich_title
    val text = if (first) Res.string.profile_nudge_first_text else Res.string.profile_nudge_enrich_text
    val cta = if (first) Res.string.profile_nudge_first_cta else Res.string.profile_nudge_enrich_cta
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.primaryContainer,
    ) {
        Box {
            Row(modifier = Modifier.padding(FruxSpacing.md)) {
                Surface(
                    modifier = Modifier.size(44.dp),
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.primary,
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = if (first) Icons.Outlined.Park else Icons.Outlined.PhotoCamera,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onPrimary,
                        )
                    }
                }
                Spacer(modifier = Modifier.width(FruxSpacing.md))
                Column(modifier = Modifier.weight(1f)) {
                    // Marge à droite : le titre ne passe pas sous la croix.
                    Text(
                        text = stringResource(title),
                        style = MaterialTheme.typography.titleMedium,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.padding(end = FruxSpacing.lg),
                    )
                    Spacer(modifier = Modifier.height(FruxSpacing.xs))
                    Text(
                        text = stringResource(text),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                    )
                    Spacer(modifier = Modifier.height(FruxSpacing.sm))
                    // Calé à droite (#592) : l'action ferme la carte visuellement, au lieu de
                    // flotter sous le texte à mi-largeur.
                    Button(
                        onClick = onPrimary,
                        modifier = Modifier.align(Alignment.End),
                        colors =
                            ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.primary,
                                contentColor = MaterialTheme.colorScheme.onPrimary,
                            ),
                    ) {
                        if (first) {
                            Icon(
                                imageVector = Icons.Outlined.Add,
                                contentDescription = null,
                                modifier = Modifier.size(18.dp),
                            )
                            Spacer(modifier = Modifier.width(FruxSpacing.xs))
                        }
                        Text(stringResource(cta))
                    }
                }
            }
            IconButton(onClick = onDismiss, modifier = Modifier.align(Alignment.TopEnd)) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.profile_nudge_dismiss),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer,
                )
            }
        }
    }
}

/**
 * Carte « Soutenir Frux » (#240, #319) : entrée permanente, visible pour tout le monde y compris
 * les donateurs, aucun montant ni jauge, juste le lien vers la page de soutien.
 */
@Composable
internal fun ProfileSupportCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.tertiaryContainer,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.tertiary.copy(alpha = 0.3f)),
        onClick = onClick,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(FruxSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Surface(
                modifier = Modifier.size(44.dp),
                shape = CircleShape,
                color = MaterialTheme.colorScheme.tertiary,
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = Icons.Outlined.VolunteerActivism,
                        contentDescription = null,
                        tint = Color.White,
                    )
                }
            }
            Spacer(modifier = Modifier.width(FruxSpacing.md))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.profile_row_support_title),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onTertiaryContainer,
                )
                Text(
                    text = stringResource(Res.string.profile_row_support_subtitle),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onTertiaryContainer,
                )
            }
            Icon(
                imageVector = Icons.Outlined.ChevronRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onTertiaryContainer,
            )
        }
    }
}

/** Bouton de déconnexion pleine largeur, pilule, couleur neutre (plus de rouge, #442). */
@Composable
internal fun ProfileLogoutButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    label: String,
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier.heightIn(min = 52.dp),
        shape = RoundedCornerShape(50),
        colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurface),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline),
    ) {
        Icon(
            imageVector = Icons.AutoMirrored.Outlined.Logout,
            contentDescription = null,
            modifier = Modifier.size(18.dp),
        )
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(text = label, style = MaterialTheme.typography.labelLarge)
    }
}

/** Lien texte souligné du pied de l'écran (déconnexion globale, suppression de compte). */
@Composable
internal fun ProfileFooterLink(
    text: String,
    onClick: () -> Unit,
    color: Color,
    modifier: Modifier = Modifier,
) {
    TextButton(
        onClick = onClick,
        modifier = modifier.heightIn(min = 44.dp),
        colors = ButtonDefaults.textButtonColors(contentColor = color),
        contentPadding = PaddingValues(horizontal = FruxSpacing.sm),
    ) {
        Text(text = text, style = MaterialTheme.typography.bodyMedium.copy(textDecoration = TextDecoration.Underline))
    }
}

/**
 * Révèle un bloc du profil dont la donnée arrive après le premier rendu (compteurs chargés du
 * réseau, carte d'accompagnement qui en dépend) : fondu et dépliage depuis le haut plutôt qu'une
 * apparition brute qui pousse d'un coup tout ce qui suit. Déjà chargée à l'entrée (retour de pile,
 * le ViewModel survit), le bloc est là sans animation. Le dernier [value] non nul reste rendu
 * pendant la sortie, pour que le repli ne vide pas la carte avant de la fermer.
 */
@Composable
internal fun <T : Any> ProfileReveal(
    value: T?,
    modifier: Modifier = Modifier,
    content: @Composable (T) -> Unit,
) {
    var lastValue by remember { mutableStateOf(value) }
    if (value != null) lastValue = value
    AnimatedVisibility(
        visible = value != null,
        modifier = modifier,
        enter =
            fadeIn(animationSpec = tween(REVEAL_FADE_MS, delayMillis = REVEAL_FADE_DELAY_MS)) +
                expandVertically(expandFrom = Alignment.Top, animationSpec = tween(REVEAL_EXPAND_MS)),
        exit =
            fadeOut(animationSpec = tween(REVEAL_FADE_MS)) +
                shrinkVertically(animationSpec = tween(REVEAL_EXPAND_MS)),
    ) {
        lastValue?.let { content(it) }
    }
}

private const val REVEAL_EXPAND_MS = 320
private const val REVEAL_FADE_MS = 220
private const val REVEAL_FADE_DELAY_MS = 80
